import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Copy, Loader2, Link2, Users, MousePointerClick, CheckCircle2, DollarSign, Megaphone, ExternalLink, Wallet } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400 mb-1"><Icon className="h-4 w-4" /><span className="text-xs font-medium uppercase tracking-wider">{label}</span></div>
      <p className="text-2xl font-semibold text-slate-900">{value}</p>
    </div>
  );
}

export default function AmbassadorDashboard() {
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [notAmb, setNotAmb] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [connectReady, setConnectReady] = useState(false);
  const [subFor, setSubFor] = useState(null);
  const [sub, setSub] = useState({ tiktok: "", instagram: "", youtube: "", notes: "" });

  const load = async () => {
    try {
      const res = await api.get("/ambassador/me");
      setData(res.data);
      const [c, cs] = await Promise.all([
        api.get("/ambassador/campaigns").catch(() => ({ data: [] })),
        api.get("/ambassador/connect/status").catch(() => ({ data: { ready: false } })),
      ]);
      setCampaigns(c.data);
      setConnectReady(cs.data.ready);
    } catch (e) {
      if (e?.response?.status === 404) setNotAmb(true);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (params.get("connected")) toast.success("Stripe onboarding complete."); }, [params]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      toast.success("Referral link copied!");
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = referralUrl; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
        toast.success("Referral link copied!");
      } catch (e2) {
        toast.error("Couldn't copy — select the link and copy it manually.");
      }
    }
  };

  const startConnect = async () => {
    try {
      const res = await api.post("/ambassador/connect/onboard", { origin_url: window.location.origin });
      window.location.href = res.data.onboarding_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start onboarding."); }
  };

  const submitCampaign = async () => {
    try {
      await api.post("/ambassador/campaigns/submit", { campaign_id: subFor.id, ...sub, screenshot_file_ids: [] });
      toast.success("Submission sent for review!");
      setSubFor(null); setSub({ tiktok: "", instagram: "", youtube: "", notes: "" });
      load();
    } catch (e) { toast.error("Could not submit."); }
  };

  if (notAmb) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-10 text-center" data-testid="not-ambassador">
        <Megaphone className="h-10 w-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-600">You're not a Syllabify ambassador yet.</p>
        <Button onClick={() => (window.location.href = "/become-ambassador")} className="mt-4 bg-blue-600 hover:bg-blue-700 rounded-lg">Apply to the program</Button>
      </div>
    );
  }
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  const { ambassador: amb, stats, ledger, submissions } = data;
  const referralUrl = `${window.location.origin}/?ref=${amb.referral_code}`;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">Ambassador dashboard</h1>
        <p className="mt-1 text-slate-500">{amb.university} · {amb.status === "active" ? "Active" : "Inactive"}</p>
      </div>

      {/* Referral link */}
      <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
        <div className="flex items-center gap-2 text-blue-700 text-sm font-medium mb-3"><Link2 className="h-4 w-4" /> Your referral link</div>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 rounded-lg bg-white border border-blue-200 px-3 py-2.5 text-sm text-slate-700 font-mono truncate" data-testid="referral-url">{referralUrl}</div>
          <Button onClick={copy} data-testid="copy-referral-btn" className="bg-blue-600 hover:bg-blue-700 rounded-lg"><Copy className="h-4 w-4 mr-1" /> Copy</Button>
        </div>
        <p className="mt-2 text-xs text-blue-700">Referral code: <span className="font-semibold">{amb.referral_code}</span></p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={MousePointerClick} label="Clicks" value={stats.clicks} testid="stat-clicks" />
        <Stat icon={Users} label="Signups" value={stats.signups} testid="stat-signups" />
        <Stat icon={CheckCircle2} label="Paid users" value={stats.paid} testid="stat-paid" />
        <Stat icon={DollarSign} label="Conversion" value={`${stats.conversion_rate}%`} testid="stat-conversion" />
      </div>

      {/* Earnings */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Earnings</h2>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div><p className="text-xs text-slate-400">Available</p><p className="text-xl font-semibold text-emerald-600" data-testid="earn-available">${stats.earnings_available.toFixed(2)}</p></div>
          <div><p className="text-xs text-slate-400">Pending</p><p className="text-xl font-semibold text-amber-600" data-testid="earn-pending">${stats.earnings_pending.toFixed(2)}</p></div>
          <div><p className="text-xs text-slate-400">Paid out</p><p className="text-xl font-semibold text-slate-900" data-testid="earn-paid">${stats.earnings_paid.toFixed(2)}</p></div>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm text-slate-600"><Wallet className="h-4 w-4" /> {connectReady ? "Payouts enabled" : "Connect a payout account to get paid"}</div>
          {!connectReady && <Button onClick={startConnect} data-testid="connect-onboard-btn" variant="outline" size="sm" className="rounded-lg">Set up payouts</Button>}
        </div>
        {ledger?.length > 0 && (
          <div className="mt-4 divide-y divide-slate-100 max-h-56 overflow-auto no-scrollbar">
            {ledger.map((l) => (
              <div key={l.id} className="flex items-center justify-between py-2 text-sm">
                <span className="text-slate-700">{l.description}</span>
                <span className={`font-medium ${l.type === "payout" ? "text-slate-400" : "text-slate-900"}`}>{l.type === "payout" ? "-" : "+"}${l.amount.toFixed(2)} <span className="text-xs text-slate-400">({l.status})</span></span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Campaigns */}
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400 mb-4">Available campaigns</h2>
        {campaigns.length === 0 ? <p className="text-sm text-slate-500">No campaigns available right now.</p> : (
          <div className="space-y-3">
            {campaigns.map((c) => (
              <div key={c.id} data-testid={`campaign-${c.id}`} className="rounded-lg border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.description} · {c.platforms?.join(", ")} · ${Number(c.payment).toFixed(2)}</p>
                </div>
                <Button onClick={() => setSubFor(c)} size="sm" data-testid={`submit-campaign-${c.id}`} className="bg-slate-900 hover:bg-slate-800 rounded-lg">Submit content</Button>
              </div>
            ))}
          </div>
        )}
        {submissions?.length > 0 && (
          <div className="mt-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">My submissions</p>
            <div className="space-y-2">
              {submissions.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm rounded-lg bg-slate-50 px-3 py-2">
                  <span className="text-slate-700">{s.campaign_name}</span>
                  <span className="text-xs"><span className="capitalize text-slate-500">{s.status}</span> · <span className="text-slate-400">pay {s.payment_status}</span></span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!subFor} onOpenChange={(o) => !o && setSubFor(null)}>
        <DialogContent data-testid="campaign-submit-modal">
          <DialogHeader><DialogTitle>Submit: {subFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="TikTok link" value={sub.tiktok} onChange={(e) => setSub({ ...sub, tiktok: e.target.value })} data-testid="sub-tiktok" />
            <Input placeholder="Instagram link" value={sub.instagram} onChange={(e) => setSub({ ...sub, instagram: e.target.value })} data-testid="sub-instagram" />
            <Input placeholder="YouTube link" value={sub.youtube} onChange={(e) => setSub({ ...sub, youtube: e.target.value })} data-testid="sub-youtube" />
            <Textarea placeholder="Notes / analytics" value={sub.notes} onChange={(e) => setSub({ ...sub, notes: e.target.value })} data-testid="sub-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubFor(null)}>Cancel</Button>
            <Button onClick={submitCampaign} data-testid="sub-submit-btn" className="bg-blue-600 hover:bg-blue-700">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
